"use client";

import React from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Search, ArrowRight } from "lucide-react";

/**
 * Deal-first quick finder — routes to the shared destinations index.
 * Market-test: `wf_homepage_search_first_v1` (above-the-fold quick plan finder
 * → search→plan click; market-test-plan.md §3). Exposure + click, consent-gated.
 */
const FINDER_EXPERIMENT_ID = "wf_homepage_search_first_v1";

export default function QuickFinder() {
  const { t } = useTranslations();

  React.useEffect(() => {
    try {
      track({
        event_name: "quick_finder_viewed",
        event_id: `quick_finder_viewed:${FINDER_EXPERIMENT_ID}`,
        experiment_id: FINDER_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onFind = () => {
    try {
      track({
        event_name: "quick_finder_clicked",
        event_id: `quick_finder_clicked:${FINDER_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: FINDER_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <section className="container mx-auto px-4 py-6">
      <div className="border-2 border-foreground bg-secondary text-secondary-foreground p-6 sm:p-8 shadow-[6px_6px_0_0_hsl(var(--foreground))] flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 border-2 border-foreground bg-card text-foreground shrink-0">
            <Search className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl">{t("cobra.finder.title", "Know where you're going?")}</h2>
            <p className="text-sm font-bold normal-case opacity-90">
              {t("cobra.finder.subtitle", "Find a plan for your destination in seconds.")}
            </p>
          </div>
        </div>
        <LocaleLink
          href="/destinations/"
          onClick={onFind}
          className="inline-flex items-center gap-2 border-2 border-foreground bg-foreground text-background font-bold uppercase tracking-tight px-6 h-12 shadow-[4px_4px_0_0_hsl(var(--background))] shrink-0 transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-0 active:translate-y-0 active:scale-[0.98]"
        >
          {t("cobra.finder.cta", "Browse destinations")} <ArrowRight className="w-4 h-4" />
        </LocaleLink>
      </div>
    </section>
  );
}
